import React, { useState, useEffect } from 'react'
import SidePanel from '@/components/ui/SidePanel'
import Input from '@/components/ui/Input'
import Button from '@/components/ui/Button'
import { designTokens } from '@/utils/design-tokens'
import { PerformanceClassService } from '@/api/generated/services/PerformanceClassService'
import type { PerformanceClassUpdateRequest } from '@/api/generated/models/PerformanceClassUpdateRequest'
import type { PerformanceClassData } from '@/api/generated/models/PerformanceClassData'

interface PerformanceClassEditPanelProps {
  isOpen: boolean
  onClose: () => void
  classData: PerformanceClassData | null
  categoryId: string
  categoryName: string
  onSuccess?: () => void
}

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div
    className="pb-2 border-b"
    style={{
      borderBottomColor: '#EBEBEB',
      borderBottomWidth: '1px',
      padding: '7px 0px'
    }}
  >
    <h3 style={{
      fontFamily: designTokens.typography.fontFamily.sans[0],
      fontSize: '18px',
      fontWeight: 700,
      color: designTokens.colors.gray[6]
    }}>
      {title}
    </h3>
  </div>
)

const InfoField: React.FC<{
  label: string
  value: string | React.ReactNode
  fullWidth?: boolean
}> = ({ label, value, fullWidth = false }) => (
  <div className={`flex ${fullWidth ? 'flex-col gap-2' : 'items-center gap-5'}`}>
    <div className={fullWidth ? '' : 'w-37 flex-shrink-0'}>
      <span style={{
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: '16px',
        fontWeight: 400,
        color: designTokens.colors.gray[6]
      }}>
        {label}
      </span>
    </div>
    <div className={fullWidth ? '' : 'flex-1 py-2'}>
      {typeof value === 'string' ? (
        <span style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: '16px',
          fontWeight: 400,
          color: designTokens.colors.gray[6]
        }}>
          {value}
        </span>
      ) : (
        value
      )}
    </div>
  </div>
)

const PerformanceClassEditPanel: React.FC<PerformanceClassEditPanelProps> = ({
  isOpen,
  onClose,
  classData,
  categoryId,
  categoryName,
  onSuccess
}) => {
  const [className, setClassName] = useState('')
  const [targetFigureFormula, setTargetFigureFormula] = useState('')
  const [doneFigureFormula, setDoneFigureFormula] = useState('')
  const [manualWeight, setManualWeight] = useState<number | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (classData) {
      setClassName(classData.name || '')
      setTargetFigureFormula(classData.target_figure_formula || '')
      setDoneFigureFormula(classData.done_figure_formula || '')
      setManualWeight(classData.manual_weight)
    }
  }, [classData])

  const handleCancel = () => {
    setClassName('')
    setTargetFigureFormula('')
    setDoneFigureFormula('')
    setManualWeight(null)
    setError(null)
    onClose()
  }

  const handleSave = async () => {
    if (!className.trim() || !classData) {
      setError('성과지표명은 필수입니다.')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const updateRequest: PerformanceClassUpdateRequest = {
        name: className.trim(),
        target_figure_formula: targetFigureFormula.trim() || null,
        done_figure_formula: doneFigureFormula.trim() || null,
        manual_weight: manualWeight
      }

      await PerformanceClassService.appsPerformanceViewsUpdatePerformanceClass({
        classId: classData.id,
        requestBody: updateRequest
      })

      handleCancel()
      onSuccess?.()
    } catch (err) {
      console.error('Failed to update performance class:', err)
      setError('성과지표 수정에 실패했습니다.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const headerActions = (
    <>
      <Button
        size="small"
        color="white"
        onClick={handleCancel}
        disabled={isSubmitting}
      >
        취소
      </Button>
      <Button
        size="small"
        color="dark"
        onClick={handleSave}
        disabled={isSubmitting || !className.trim()}
      >
        {isSubmitting ? '저장 중...' : '저장'}
      </Button>
    </>
  )

  if (!classData) return null

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={handleCancel}
      title="성과지표 수정"
      width={800}
      headerActions={headerActions}
    >
      <div
        className="flex flex-col space-y-6"
        style={{
          padding: '24px',
        }}
      >
        <div className="space-y-4">
          <SectionHeader title="기본 정보" />
          <div className="space-y-4" style={{ padding: '0px 4px' }}>
            <InfoField
              label="상위 하위분류"
              value={categoryName || '-'}
            />
            <InfoField
              label="성과지표명"
              value={
                <Input
                  type="text"
                  placeholder="성과지표명을 입력하세요"
                  value={className}
                  onChange={(e) => setClassName(e.target.value)}
                  disabled={isSubmitting}
                />
              }
            />
          </div>
        </div>

        <div className="space-y-4">
          <SectionHeader title="수치 설정" />
          <div className="space-y-4" style={{ padding: '0px 4px' }}>
            <InfoField
              label="목표수치 공식"
              value={
                <Input
                  type="text"
                  placeholder="예: SUM(P001, P002)"
                  value={targetFigureFormula}
                  onChange={(e) => setTargetFigureFormula(e.target.value)}
                  disabled={isSubmitting}
                />
              }
            />
            <InfoField
              label="달성수치 공식"
              value={
                <Input
                  type="text"
                  placeholder="예: SUM(P001, P002)"
                  value={doneFigureFormula}
                  onChange={(e) => setDoneFigureFormula(e.target.value)}
                  disabled={isSubmitting}
                />
              }
            />
            <InfoField
              label="수동 가중치"
              value={
                <Input
                  type="number"
                  placeholder="자동 계산하려면 비워두세요"
                  value={manualWeight ?? ''}
                  onChange={(e) => {
                    const value = e.target.value
                    setManualWeight(value ? parseFloat(value) : null)
                  }}
                  disabled={isSubmitting}
                  min={0}
                  max={100}
                  step={0.01}
                />
              }
            />
          </div>
        </div>

        {/* 수식 입력 안내 */}
        <div>
          <SectionHeader title="수식 입력 안내" />
          <div
            className="flex flex-col gap-4 mt-4"
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: designTokens.typography.fontSize['body-small'][0],
              color: designTokens.colors.gray[6],
              lineHeight: '1.6'
            }}
          >
            <p style={{ color: designTokens.colors.gray[5] }}>
              목표수치 수식과 달성수치 수식은 아래 3가지 방법으로 입력할 수 있습니다.
            </p>

            {/* 1. 기본 계산 */}
            <div className="flex flex-col gap-1">
              <span style={{ fontWeight: 600 }}>1. 기본 계산 (공백)</span>
              <span style={{ color: designTokens.colors.gray[5] }}>
                수식 입력란을 비워두면 하위 세부지표 값의 합계(sum)로 자동 계산됩니다.
              </span>
            </div>

            {/* 2. 숫자 입력 */}
            <div className="flex flex-col gap-1">
              <span style={{ fontWeight: 600 }}>2. 숫자 입력</span>
              <span style={{ color: designTokens.colors.gray[5] }}>
                숫자만 입력하면 해당 값이 그대로 적용됩니다.
              </span>
              <code
                style={{
                  padding: '4px 8px',
                  backgroundColor: designTokens.colors.gray[0],
                  borderRadius: '4px',
                  fontSize: designTokens.typography.fontSize['body-xsmall'][0],
                  color: designTokens.colors.gray[6],
                  width: 'fit-content'
                }}
              >
                예: 400
              </code>
            </div>

            {/* 3. 수식 입력 */}
            <div className="flex flex-col gap-1">
              <span style={{ fontWeight: 600 }}>3. 수식 입력</span>
              <span style={{ color: designTokens.colors.gray[5] }}>
                하위 세부지표를 <code style={{ padding: '1px 4px', backgroundColor: designTokens.colors.gray[0], borderRadius: '2px' }}>$번호</code> 형태로 참조하여 수식을 작성합니다.
              </span>
              <div className="flex flex-col gap-1 mt-1">
                <code
                  style={{
                    padding: '4px 8px',
                    backgroundColor: designTokens.colors.gray[0],
                    borderRadius: '4px',
                    fontSize: designTokens.typography.fontSize['body-xsmall'][0],
                    color: designTokens.colors.gray[6],
                    width: 'fit-content'
                  }}
                >
                  예: $1 + 200 + $2 * 1.2
                </code>
                <code
                  style={{
                    padding: '4px 8px',
                    backgroundColor: designTokens.colors.gray[0],
                    borderRadius: '4px',
                    fontSize: designTokens.typography.fontSize['body-xsmall'][0],
                    color: designTokens.colors.gray[6],
                    width: 'fit-content'
                  }}
                >
                  예: ($1 + 10) * $2 + 30
                </code>
              </div>
            </div>

            {/* 입력 가능 값 */}
            <div
              className="flex flex-col gap-1"
              style={{
                padding: '8px 12px',
                backgroundColor: designTokens.colors.gray[0],
                borderRadius: '6px',
                borderLeft: `3px solid ${designTokens.colors.primary.DEFAULT}`
              }}
            >
              <span style={{ fontWeight: 600, fontSize: designTokens.typography.fontSize['body-xsmall'][0] }}>입력 가능한 값</span>
              <span style={{ color: designTokens.colors.gray[5], fontSize: designTokens.typography.fontSize['body-xsmall'][0] }}>
                숫자, 세부지표 참조($), 사칙연산자(+, -, *, /), 괄호( )
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: designTokens.colors.error.light,
            borderRadius: '4px',
            color: designTokens.colors.error.dark
          }}>
            {error}
          </div>
        )}
      </div>
    </SidePanel>
  )
}

export default PerformanceClassEditPanel